import { useMemo } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { Separator } from './ui/separator'
import { mdiDownload, mdiPencil } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { usePropertyDefinition, usePropertyEnumValues } from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { usePropertyExport } from '../hooks/usePropertyExport'
import { useToast } from '../hooks/use-toast'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'

export function PropertyDetailPage() {
  const { org, propertyUid } = useParams({ strict: false })
  const navigate = useNavigate()
  const { exportProperty } = usePropertyExport()
  const { toast } = useToast()
  const { t } = useTranslation('properties')

  const { data: property, isLoading, error } = usePropertyDefinition(org || 'demo', propertyUid || '')
  const { data: enumValues = [] } = usePropertyEnumValues(org || 'demo', propertyUid || '', {
    enabled: property?.data_type === 'enum'
  })

  // Helper function to handle multilingual property names
  const getPropertyName = (property: any) => {
    if (!property?.name) return property?.code || 'Loading...'

    // Handle multilingual names
    if (typeof property.name === 'object' && property.name !== null) {
      const names = property.name as Record<string, string>
      return names.en || names.fr || Object.values(names)[0] || property.code || 'Loading...'
    }

    return property.name || property.code || 'Loading...'
  }

  // Helper function to handle multilingual descriptions
  const getPropertyDescription = (property: any) => {
    if (!property?.description) return ''

    // Handle multilingual descriptions
    if (typeof property.description === 'object' && property.description !== null) {
      const descriptions = property.description as Record<string, string>
      return descriptions.en || descriptions.fr || Object.values(descriptions)[0] || ''
    }

    return property.description || ''
  }

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('mapping_rules.breadcrumb_home'), path: `/${org}` },
    { title: t('list.title'), path: `/${org}/properties` },
    { title: getPropertyName(property) }
  ], [org, property, t])

  useBreadcrumbs(breadcrumbs)

  const handleEdit = () => {
    navigate({ to: `/${org}/properties/${propertyUid}/edit` })
  }

  const handleExport = async () => {
    if (!property) return

    try {
      await exportProperty(property)
    } catch {
      toast({
        title: t('common:status.error'),
        description: t('detail.export_error'),
        variant: "destructive",
      })
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="bg-card rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-center">
            <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
            <span className="text-muted-foreground">{t('detail.loading')}</span>
          </div>
        </div>
      </div>
    )
  }

  if (error || !property) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="bg-card rounded-lg shadow-lg p-6">
          <div className="flex flex-col items-center justify-center">
            <p className="text-destructive mb-2">{t('detail.error')}</p>
            <p className="text-sm text-muted-foreground mb-4">
              {(error as any)?.title || (error as any)?.message || t('detail.not_found')}
            </p>
            <Button onClick={() => navigate({ to: `/${org}/properties` as any })}>
              {t('detail.back')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const sortedEnumValues = [...enumValues].sort((a, b) => a.order_index - b.order_index)

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="bg-card rounded-lg shadow-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border">
          <PageHeader
            icon={SECTION_ICONS.properties}
            title={getPropertyName(property)}
            titleAside={
              <>
                <code className="text-sm bg-muted px-2 py-1 rounded">
                  {property.code}
                </code>
                <Badge variant="secondary">
                  {t(`data_types.${property.data_type}`)}
                </Badge>
              </>
            }
            actions={
            <>
              <Button onClick={handleEdit} data-testid="edit-property">
                <AppIcon path={mdiPencil} size="sm" className="mr-2" />
                {t('detail.edit')}
              </Button>
              <Button
                variant="outline"
                onClick={handleExport}
                data-testid="export-property-btn"
              >
                <AppIcon path={mdiDownload} size="sm" className="mr-2" />
                {t('detail.export')}
              </Button>
            </>
            }
          />
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          {getPropertyDescription(property) && (
            <div className="space-y-2">
              <h3 className="text-lg font-medium">{t('detail.description_title')}</h3>
              <p className="text-muted-foreground">{getPropertyDescription(property)}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium">{t('detail.configuration_title')}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-muted-foreground">{t('detail.data_type_label')}</span>
                <p className="mt-1">{t(`data_types.${property.data_type}`)}</p>
              </div>
              {property.data_type === 'enum' && property.properties?.multiple ? (
                <div>
                  <span className="text-sm font-medium text-muted-foreground">{t('detail.multiple_selection_label')}</span>
                  <p className="mt-1">{t('detail.multiple_selection_enabled')}</p>
                </div>
              ) : null}
            </div>
          </div>

          {/* Target Entity Types */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium">{t('detail.target_entity_types_title')}</h3>
            <div className="flex flex-wrap gap-2">
              {property.target_entity_types?.map((entityType) => (
                <Badge key={entityType} variant="outline">
                  {t(`entity_types.${entityType}`, { defaultValue: entityType })}
                </Badge>
              ))}
            </div>
          </div>

          {/* Value suggestions (spec 2026-09-02-03) — read-only mirror of the
              Suggestions card on the edit page. */}
          {property.data_type === 'text' && (
            <>
              <Separator />
              <div className="space-y-3" data-testid="property-suggestions-summary">
                <h3 className="text-lg font-medium">{t('settings.suggestions.title')}</h3>
                {(() => {
                  const suggestions = property.properties?.suggestions as
                    | { scope?: string; depends_on?: string[] }
                    | undefined
                  if (!suggestions) {
                    return <p className="text-muted-foreground">{t('settings.suggestions.none')}</p>
                  }
                  const scope = suggestions.scope === 'organization' ? 'organization' : 'asset_type'
                  const dependsOn = suggestions.depends_on ?? []
                  return (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm font-medium text-muted-foreground">{t('settings.suggestions.scope')}</span>
                        <p className="mt-1">{t(`settings.suggestions.scope_${scope}`)}</p>
                      </div>
                      {dependsOn.length > 0 ? (
                        <div>
                          <span className="text-sm font-medium text-muted-foreground">{t('settings.suggestions.depends_on')}</span>
                          <div className="mt-1 flex flex-wrap gap-2">
                            {dependsOn.map(code => (
                              <Badge key={code} variant="outline">{code}</Badge>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )
                })()}
              </div>
            </>
          )}

          {/* Select Values */}
          {property.data_type === 'enum' && (
            <>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">{t('detail.select_values_title')}</h3>
                  <span className="text-sm text-muted-foreground">
                    {t('detail.select_values_count_other', { count: sortedEnumValues.length })}
                  </span>
                </div>
                {sortedEnumValues.length === 0 ? (
                  <EmptyState
                    title={t('detail.no_select_values')}
                    description={t('detail.no_select_values_hint')}
                  />
                ) : (
                  <div className="space-y-2">
                    {sortedEnumValues.map((enumValue, index) => (
                      <div
                        key={enumValue.uid}
                        className="flex items-center justify-between p-3 border border-border rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-sm text-muted-foreground w-6">
                            {index + 1}.
                          </span>
                          <div>
                            <code className="text-sm bg-muted px-2 py-1 rounded">
                              {enumValue.value}
                            </code>
                            <span className="ml-2">{enumValue.name?.en || enumValue.name?.fr || Object.values(enumValue.name || {})[0] || t('detail.unnamed')}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Usage Information */}
          <Separator />
          <div className="space-y-3">
            <h3 className="text-lg font-medium">{t('detail.usage_title')}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-muted-foreground">{t('detail.created_label')}</span>{' '}
                {property.created_at ? new Date(property.created_at).toLocaleDateString() : t('detail.unknown')}
              </div>
              <div>
                <span className="font-medium text-muted-foreground">{t('detail.updated_label')}</span>{' '}
                {property.updated_at ? new Date(property.updated_at).toLocaleDateString() : t('detail.unknown')}
              </div>
            </div>

            {/* Usage Statistics Placeholder */}
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <h4 className="text-sm font-medium mb-2">{t('detail.usage_stats_title')}</h4>
              <p className="text-sm text-muted-foreground">
                {t('detail.usage_stats_placeholder')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}